import { FormSectionHandler } from '../entity.handler';
import { RoadProfileResponse, RoadProfileUpdateRequest, RoadSpeedModifierEntry } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, GeoFilterField, NumberField, ComboField } from '../utils';
import { ICONS } from '../icons';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import * as uuid from 'uuid';
import { GeoFilterService } from 'app/services';


interface EditorValues {
    name?: string;
    modifiers?: ListFieldValue<RoadSpeedModifierEntry>[];
}

export class RoadProfileDetailsSectionHandler implements FormSectionHandler<RoadProfileResponse, EditorValues> {

    type = 'form';
    icon = ICONS.roadprofile;
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');
    warning = null;

    constructor(
        public admin: AdminEditorService,
        private filters: GeoFilterService,
        public ownerId: string,
        private creating: boolean
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.ROADPROFILE.DETAILS.${key}`, values);
    }

    applyUpdate(entity: RoadProfileResponse, payload: RoadProfileUpdateRequest): Promise<RoadProfileResponse> {
        return this.admin.applyUpdate(api => api.entities.updateRoadProfile(entity.id, payload)) as any; // TODO remove any
    }

    mapEntityToEditor(entity: RoadProfileResponse): Promise<EditorValues> {
        const result = {
            name: entity.name,
            modifiers: (entity.speedModifiers && entity.speedModifiers.map(x => this.stateItemToListItem(x))) || [],
        };
        Object.keys(result).forEach(key => result[key] === undefined && delete result[key]); // strip undefined properties from the object
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: RoadProfileUpdateRequest, values: EditorValues): Promise<RoadProfileUpdateRequest> {
        const patch: RoadProfileUpdateRequest = {
            name: values.name,
            speedModifiers: values.modifiers ? values.modifiers.map(x => x.data) : undefined,
        };
        Object.keys(patch).forEach(key => patch[key] === undefined && delete patch[key]); // strip undefined properties from the object
        return Promise.resolve(EntityUtils.mergeFields(['name', 'speedModifiers'], patch, entity));
    }

    getFields(entity: RoadProfileResponse): Promise<FormBuilderField[]> {
        return Promise.resolve().then(() => {
            return [
                TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                {
                    id: 'modifiers', title: this.translate('FIELDS.ITEMS'), type: 'list', required: false, visible: !this.creating,
                    actions: [
                        { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showFilterModal.bind(this), },
                        { id: 'edit', click: this.showFilterModal.bind(this), },
                    ],
                    getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
                },
            ];
        }).then(fields => EntityUtils.prepareBulk(!entity, [], fields));

    }

    stateItemToListItem(item: RoadSpeedModifierEntry): ListFieldValue<RoadSpeedModifierEntry> {
        return {
            id: item.id,
            name: `${item.name} (${item.speed} ${item.unit})`,
            icon: ICONS.overspeedprofile,
            description: this.filters.getEventFilterText(item.filter),
            data: item,
        };
    }

    updateOrAddItem(items: ListFieldValue<RoadSpeedModifierEntry>[], existingItem: ListFieldValue<RoadSpeedModifierEntry>, item: RoadSpeedModifierEntry) {
        if (existingItem) {
            const idx = items.findIndex(x => x.id === existingItem.id);
            items[idx] = this.stateItemToListItem(item);
        } else {
            items.push(this.stateItemToListItem(item));
        }
    }

    showFilterModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<any>): Promise<RoadSpeedModifierEntry> {
        return this.admin.showFormModal<RoadSpeedModifierEntry>({
            groups: [{
                name: this.translate('MODALS.MODIFIER.TITLE'),
                description: this.translate('MODALS.MODIFIER.DESC'),
                fields: [
                    TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                    GeoFilterField('filter', this.translate('FIELDS.FILTER'), true, this.ownerId, false, this.filters),
                    NumberField('speed', this.translate('FIELDS.SPEED'), true, 0, 1000, this.admin.units.format(0, 'speed').unit),
                    ComboField('unit', this.translate('FIELDS.SPEED'), true, [{ key: 'kmh', value: 'km/h' }, { key: 'mph', value: 'mph' }]),
                ],
            }],
        }, (item && item.data) || { // pass some defaults for new items
            id: uuid.v4(),
            name: '',
            filter: {
                action: {
                    eventClass: 'geocoding',
                    eventType: 'speed',
                    text: this.admin.i18n.instant(`SHARED.EVENTS.GEOCODING.TYPES.SPEED.ACTION`),
                },
                conditions: [],

            },
            unit: this.admin.units.format(0, 'speed').unit === 'mph' ? 'mph' : 'kmh',
        }).then(result => {
            if (result) {
                console.log(result);
                this.updateOrAddItem(values.modifiers, item, result);
            }
            return result;
        });


    }



}
